function getErrorMessage(error: unknown): string {
  if (typeof error === "string") return error;
  if (error instanceof Error) return error.message;
  if (error && typeof error === "object" && "message" in error) {
    const { message } = error as { message: unknown };
    return typeof message === "string" ? message : "";
  }
  return "";
}

const TRANSIENT_NETWORK_PATTERNS = [
  "load failed",
  "failed to fetch",
  "network error",
  "err_network_changed",
  "network changed",
  "network request failed",
] as const;

/** Safari/iOS and mobile browsers use varied wording for flaky connectivity. */
export function isTransientNetworkError(error: unknown): boolean {
  const message = getErrorMessage(error).toLowerCase();
  if (!message) return false;
  return TRANSIENT_NETWORK_PATTERNS.some((pattern) => message.includes(pattern));
}

export const TRANSIENT_GET_RETRY_DELAYS_MS = [500, 1500] as const;

export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/**
 * Fetch With Transient Get Retry.
 */
export async function fetchWithTransientGetRetry(
  fetchFn: () => Promise<Response>,
  method: string,
  isRetryableError: (error: unknown) => boolean
): Promise<Response> {
  const isGet = method.toUpperCase() === "GET";
  const delays = isGet ? TRANSIENT_GET_RETRY_DELAYS_MS : [];
  const maxAttempts = 1 + delays.length;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    try {
      return await fetchFn();
    } catch (error) {
      const hasRetry = attempt < delays.length;
      if (hasRetry && isRetryableError(error)) {
        await sleep(delays[attempt]);
        continue;
      }
      throw error;
    }
  }

  throw new Error("fetchWithTransientGetRetry: unreachable");
}
